import { useCallback, useEffect, useMemo, useState } from 'react';
import Swal from 'sweetalert2';
import { API_BASE_URL } from '../config/env';
import { buildCrmSwalBody, fireConfirmDialog } from '../utils/swalConfirm';
import { CwListSearch } from '../components/conversaciones/CwUiParts';
import AmvTablePager from '../components/admin/AmvTablePager';
import '../styles/conversaciones-premium.css';
import '../styles/gestion-derivacion-premium.css';
import '../styles/admin-medicos-visibilidad-premium.css';

const ENDPOINTS = {
  list: '/api/asociacion-recetas/medicos/visibilidad',
  patch: (id) => `/api/asociacion-recetas/medicos/${id}/visibilidad`
};

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

function formatDoctorDisplayName(nombre) {
  const raw = String(nombre || '').trim();
  if (!raw) return '—';
  if (/^(dr|dra)\.?\s/i.test(raw)) return raw;
  return `Dr/a. ${raw}`;
}

function sortMedicosByName(list) {
  return [...list].sort((a, b) => {
    const aVisible = Number(a?.is_visible) === 1 ? 1 : 0;
    const bVisible = Number(b?.is_visible) === 1 ? 1 : 0;
    // First sort by visibility (visible/activo first)
    if (aVisible !== bVisible) return bVisible - aVisible;
    // Then sort by name within each group
    const aName = String(a?.nombre || '').trim();
    const bName = String(b?.nombre || '').trim();
    return aName.localeCompare(bName, 'es', { sensitivity: 'base' });
  });
}

function AdminMedicosVisibilidad() {
  const [medicos, setMedicos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [savingIds, setSavingIds] = useState({});

  const loadMedicos = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE_URL}${ENDPOINTS.list}`, { credentials: 'include' });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.message || json?.error || `HTTP ${res.status}`);
      const list = Array.isArray(json?.medicos) ? json.medicos : [];
      setMedicos(sortMedicosByName(list));
    } catch (e) {
      setError(e.message || 'No se pudo cargar el listado de médicos.');
      setMedicos([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadMedicos();
  }, [loadMedicos]);

  const sortedMedicos = useMemo(() => sortMedicosByName(medicos), [medicos]);

  const filteredMedicos = useMemo(() => {
    const q = String(search || '').trim().toLowerCase();
    if (!q) return sortedMedicos;
    return sortedMedicos.filter((m) => {
      const nombre = String(m.nombre || '').toLowerCase();
      const esp = String(m.especialidad || '').toLowerCase();
      const mat = String(m.matricula || '').toLowerCase();
      const shown = formatDoctorDisplayName(m.nombre).toLowerCase();
      return nombre.includes(q) || shown.includes(q) || esp.includes(q) || mat.includes(q);
    });
  }, [sortedMedicos, search]);

  const totalPages = useMemo(() => {
    if (pageSize <= 0) return 1;
    return Math.max(1, Math.ceil(filteredMedicos.length / pageSize));
  }, [filteredMedicos.length, pageSize]);

  useEffect(() => {
    if (page > totalPages) setPage(1);
  }, [page, totalPages]);

  const pageItems = useMemo(() => {
    if (pageSize <= 0) return filteredMedicos;
    const start = (page - 1) * pageSize;
    return filteredMedicos.slice(start, start + pageSize);
  }, [filteredMedicos, page, pageSize]);

  const rangeStart = filteredMedicos.length === 0
    ? 0
    : pageSize <= 0
      ? 1
      : (page - 1) * pageSize + 1;
  const rangeEnd = pageSize <= 0
    ? filteredMedicos.length
    : Math.min(page * pageSize, filteredMedicos.length);

  const visibleCount = useMemo(
    () => medicos.filter((m) => Number(m.is_visible) === 1).length,
    [medicos]
  );
  const hiddenCount = Math.max(0, medicos.length - visibleCount);

  const searchActive = Boolean(String(search || '').trim());

  async function requestToggleVisibilidad(medico, currentlyVisible) {
    const nextVisible = !currentlyVisible;
    const nombre = formatDoctorDisplayName(medico.nombre);
    const details = [
      { label: 'Médico', value: nombre },
      ...(medico.especialidad ? [{ label: 'Especialidad', value: String(medico.especialidad) }] : []),
      ...(medico.matricula ? [{ label: 'Matrícula', value: String(medico.matricula) }] : [])
    ];

    const confirm = await fireConfirmDialog({
      title: nextVisible ? 'Mostrar en derivación' : 'Ocultar de derivación',
      html: buildCrmSwalBody({
        lead: nextVisible
          ? 'El profesional aparecerá en el selector «Derivar a médico» de Gestión Derivación.'
          : 'El profesional dejará de aparecer en el selector «Derivar a médico».',
        details,
        items: nextVisible
          ? ['Podrá ser elegido al derivar pedidos', 'El cambio es inmediato para todo el equipo']
          : ['No se elimina del catálogo', 'Podés reactivarlo cuando quieras desde esta pantalla'],
        footnote: nextVisible
          ? 'Confirmá solo si este médico debe recibir derivaciones.'
          : 'Los pedidos ya derivados a este médico no se modifican.'
      }),
      icon: nextVisible ? 'question' : 'warning',
      confirmButtonText: nextVisible ? 'Mostrar médico' : 'Ocultar médico',
      cancelButtonText: 'Cancelar',
      width: 'min(420px, 92vw)'
    });

    if (!confirm.isConfirmed) return;
    await toggleVisibilidad(medico, nextVisible);
  }

  async function toggleVisibilidad(medico, nextVisible) {
    const id = Number(medico.id);
    if (!Number.isFinite(id) || id <= 0) return;

    setSavingIds((prev) => ({ ...prev, [id]: true }));
    try {
      const res = await fetch(`${API_BASE_URL}${ENDPOINTS.patch(id)}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_visible: nextVisible ? 1 : 0 })
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.message || json?.error || `HTTP ${res.status}`);

      const updated = json.medico || { ...medico, is_visible: nextVisible ? 1 : 0 };
      setMedicos((prev) => sortMedicosByName(
        prev.map((m) => (Number(m.id) === id ? { ...m, ...updated } : m))
      ));
    } catch (e) {
      Swal.fire({
        icon: 'error',
        title: 'No se pudo actualizar',
        text: e.message || 'Error al cambiar la visibilidad del médico.'
      });
    } finally {
      setSavingIds((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
    }
  }

  if (loading) {
    return (
      <div className="crm-gd-loading" role="status" aria-live="polite" aria-busy="true" style={{ height: '100vh', margin: '15px' }}>
        <div className="crm-gd-spinner" aria-hidden="true" />
        <div className="crm-gd-loading__title">Cargando médicos</div>
        <p style={{ margin: 0, fontSize: 12, color: '#64748b' }}>Consultando visibilidad en derivación…</p>
      </div>
    );
  }

  return (
    <section className="crm-amv-page crm-gd-page crm-pbody">
      {error ? (
        <div className="crm-amv-alert" role="alert">{error}</div>
      ) : null}

      <section
        className="crm-amv-hero-panel crm-amv-hero-panel--medicos"
        aria-labelledby="amv-hero-title"
      >
        <div className="crm-amv-hero-panel__head">
          <div className="crm-amv-hero-panel__brand">
            <div className="crm-amv-hero-panel__icon" aria-hidden="true">🧑‍⚕️</div>
            <div className="crm-amv-hero-panel__titles">
              <p className="crm-amv-hero-panel__eyebrow">Catálogo de médicos</p>
              <h2 id="amv-hero-title" className="crm-amv-hero-panel__title">
                Visibilidad en derivación
              </h2>
            </div>
          </div>
          <div className="crm-amv-hero-panel__stats" aria-label="Resumen del catálogo">
            <div className="crm-amv-hero-stat">
              <span className="crm-amv-hero-stat__num">{medicos.length}</span>
              <span className="crm-amv-hero-stat__label">Total</span>
            </div>
            <div className="crm-amv-hero-stat crm-amv-hero-stat--visible">
              <span className="crm-amv-hero-stat__num">{visibleCount}</span>
              <span className="crm-amv-hero-stat__label">Activos</span>
            </div>
            <div className="crm-amv-hero-stat crm-amv-hero-stat--hidden">
              <span className="crm-amv-hero-stat__num">{hiddenCount}</span>
              <span className="crm-amv-hero-stat__label">Inactivos</span>
            </div>
          </div>
        </div>
      </section>

      <div className="crm-amv-card crm-gd-card">
        <div className="crm-amv-datatable-toolbar">
          <div className="crm-amv-datatable-toolbar__search">
            <CwListSearch
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Buscar por nombre, especialidad o matrícula…"
            />
          </div>
          <div className="crm-amv-datatable-toolbar__field">
            <label htmlFor="amv-page-size">Mostrar</label>
            <select
              id="amv-page-size"
              className="crm-amv-select"
              value={pageSize <= 0 ? 'ALL' : String(pageSize)}
              onChange={(e) => {
                const v = e.target.value;
                setPageSize(v === 'ALL' ? 0 : Number(v));
                setPage(1);
              }}
              aria-label="Registros por página"
            >
              {PAGE_SIZE_OPTIONS.map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
              <option value="ALL">Todos</option>
            </select>
            <span className="crm-amv-datatable-toolbar__suffix">por página</span>
          </div>
        </div>

        {medicos.length === 0 ? (
          <div className="crm-amv-state crm-amv-state--empty" role="status">
            <span className="crm-amv-state__icon" aria-hidden="true">⚕️</span>
            <p className="crm-amv-state__title">Sin médicos en catálogo</p>
            <p className="crm-amv-state__hint">No hay profesionales cargados en la tabla de médicos.</p>
          </div>
        ) : filteredMedicos.length === 0 ? (
          <div className="crm-amv-state crm-amv-state--no-match" role="status">
            <span className="crm-amv-state__icon" aria-hidden="true">🔎</span>
            <p className="crm-amv-state__title">Sin coincidencias</p>
            {searchActive ? (
              <p className="crm-amv-state__term" title={search.trim()}>
                {search.trim().length > 48 ? `${search.trim().slice(0, 48)}…` : search.trim()}
              </p>
            ) : null}
            <p className="crm-amv-state__hint">
              No hay médicos que coincidan con la búsqueda. Probá con otro texto o borrá el filtro.
            </p>
            <button
              type="button"
              className="crm-amv-state__btn"
              onClick={() => { setSearch(''); setPage(1); }}
            >
              Limpiar búsqueda
            </button>
          </div>
        ) : (
          <>
            <div className="crm-amv-table-wrap">
              <table className="crm-amv-table">
                <thead>
                  <tr>
                    <th>Médico</th>
                    <th>Estado</th>
                    <th className="crm-amv-table__th-switch">Visibilidad</th>
                  </tr>
                </thead>
                <tbody>
                  {pageItems.map((m) => {
                    const isVisible = Number(m.is_visible) === 1;
                    const isSaving = Boolean(savingIds[Number(m.id)]);
                    return (
                      <tr
                        key={m.id}
                        className={`crm-amv-table__row${isVisible ? ' is-visible' : ''}${isSaving ? ' is-saving' : ''}`}
                      >
                        <td>
                          <div className="crm-amv-table__doctor">
                            {isVisible ? (
                              <span className="crm-amv-table__doctor-dot" aria-hidden="true" />
                            ) : null}
                            <span className="crm-amv-table__doctor-name">
                              {formatDoctorDisplayName(m.nombre)}
                            </span>
                          </div>
                        </td>
                        <td>
                          {isVisible ? (
                            <span className="crm-amv-pill crm-amv-pill--visible">Visible</span>
                          ) : (
                            <span className="crm-amv-pill crm-amv-pill--hidden">Oculto</span>
                          )}
                        </td>
                        <td className="crm-amv-table__switch-cell">
                          <label
                            className="crm-amv-row__switch-wrap"
                            onClick={(e) => {
                              e.preventDefault();
                              if (isSaving) return;
                              void requestToggleVisibilidad(m, isVisible);
                            }}
                          >
                            <span className="crm-amv-row__switch-label">
                              {isVisible ? 'Activo' : 'Inactivo'}
                            </span>
                            <span className="crm-amv-switch-control">
                              <input
                                className="crm-amv-switch-input"
                                type="checkbox"
                                checked={isVisible}
                                readOnly
                                tabIndex={-1}
                                aria-hidden="true"
                              />
                              <span
                                className="crm-amv-switch-track"
                                role="switch"
                                aria-checked={isVisible}
                                aria-label={`${isVisible ? 'Ocultar' : 'Mostrar'} a ${m.nombre} en derivación`}
                              />
                            </span>
                          </label>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <AmvTablePager
              rangeStart={rangeStart}
              rangeEnd={rangeEnd}
              filteredCount={filteredMedicos.length}
              totalCount={medicos.length}
              searchActive={searchActive}
              page={page}
              totalPages={totalPages}
              pageSize={pageSize}
              onPageChange={setPage}
              entityLabel="médicos"
            />
          </>
        )}
      </div>
    </section>
  );
}

export default AdminMedicosVisibilidad;
